import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBans } from '../../../hooks/useBans';
import { setCanSendExternalChatLink, setShowQrModal } from '../../../reduxToolkit/slices/videoSlice';
import { useSelector, useDispatch } from '../../../reduxToolkit/typedRedux';
import { clearChat, getChatMessages, getMeeting, getUserBans, sendChatLink, toggleChat } from '../../../services/services';
import { setIsBannedFromChat, setShowNewMessageIndicatorInMeeting } from '../../../reduxToolkit/slices/chatSlice';
import { MeetingChatMessage } from '../../../interfaces/interfaces';
import { useSounds } from '../../../hooks/useSounds';
import { store } from '../../../reduxToolkit/store';

export function useChat(meetingId?: string) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isBannedFromChat } = useBans();
  const { playChatMessage } = useSounds();

  const meeting = useSelector(({ video }) => video.meeting);
  const meetingChannel = useSelector(({ common }) => common.pusherChannels.meetingChannel);
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);

  const [loadedAllMessages, setLoadedAllMessages] = useState(false); // Only set to true when loadMessages did not change the state of messages
  const [onChatReload, setOnChatReload] = useState<{ action: () => void } | undefined>();
  const [onMeetingChat, setOnMeetingChat] = useState<{ action: (data: { msg: MeetingChatMessage }) => void } | undefined>();
  const [onChatHighlight, setOnChatHighlight] = useState<{ action: (data: { msg: MeetingChatMessage }) => void } | undefined>();
  const [onMessageDeleted, setOnMessageDeleted] = useState<{ action: (data: { messageId: string }) => void } | undefined>();
  const [canChat, setCanChat] = useState(true);

  function init() {
    loadMessages();
    checkIfUserIsBannedFromChat();
  }

  async function loadMessages(offset?: number, meetingId?: string, minuteOffset?: number) {
    const newMessages = await getChatMessages(meetingId ?? meeting?._id!, minuteOffset);
    return newMessages;
  }

  async function checkIfUserIsBannedFromChat() {
    if (meeting?.meetingtype === 'temporary' || !event) return;
    const userbans = await getUserBans(event?._id);

    // This rather plain check should be enough for now, since the api already filters for bans of type chat
    dispatch(setIsBannedFromChat(isBannedFromChat(userbans)));
  }

  async function toggleChatLock(pageId?: string) {
    await toggleChat(pageId, !pageId ? meetingId ?? meeting?._id! : undefined);
  }

  function setupChatChannel() {
    if (!meetingChannel) return;

    if (!meetingChannel.callbacks.get('togglechat')) {
      meetingChannel?.bind('togglechat', (data: { canChat: boolean }) => {
        setCanChat(data.canChat);
        toast(t(`meeting:chat.${data.canChat ? 'can-chat' : 'locked'}-toast`));
      });
    }

    if (!meetingChannel.callbacks.get('chatreload')) {
      meetingChannel?.bind('chatreload', () => {
        toast(t('meeting:chat-cleared'));
        onChatReload?.action();
        init();
      });
    }

    if (!meetingChannel.callbacks.get('chathighlight')) {
      meetingChannel?.bind('chathighlight', (data: { msg: MeetingChatMessage }) => {
        onChatHighlight?.action(data);
      });
    }

    if (!meetingChannel.callbacks.get('message-deleted')) {
      meetingChannel?.bind('message-deleted', (data: { messageId: string }) => {
        onMessageDeleted?.action(data);
      });
    }

    if (!meetingChannel.callbacks.get(`meetingchat-${meetingId ?? meeting?._id}`)) {
      meetingChannel?.bind(`meetingchat-${meetingId ?? meeting?._id}`, (data: { msg: MeetingChatMessage }) => {
        // Make sure that the incoming message belongs to the currently opened meeting because it might still receive messages from previously opened meetings
        if (data.msg.meeting && !store.getState().common.pusherChannels.meetingChannel?.name.includes(data.msg.meeting)) {
          return;
        }

        onMeetingChat?.action(data);
        dispatch(setShowNewMessageIndicatorInMeeting(true));

        const state = store.getState();
        if (state.chat.playChatSound && user?._id !== data.msg?.user?._id) {
          playChatMessage();
        }
      });
    }
  }

  function teardownChatChannel() {
    if (meetingChannel) {
      meetingChannel.unbind('chatreload');
      meetingChannel.unbind('togglechat');
      meetingChannel.unbind('chathighlight');
      meetingChannel.unbind('message-deleted');
      meetingChannel.unbind(`meetingchat-${meetingId ?? meeting?._id}`);
    }
  }

  async function handleSendExternalChatLink() {
    dispatch(setCanSendExternalChatLink(false));
    try {
      await sendChatLink(meetingId ?? meeting?._id!);
      toast.success(t('meeting:toasts.send-external-chat-link'));
    } catch (err) {
      console.error((err as any)?.toString());
    }
  }

  function showQr() {
    dispatch(setShowQrModal(true));
  }

  async function deleteMessages(meetingId: string) {
    try {
      await clearChat(meetingId);
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchAndSetChatLockState() {
    if (!meeting) return;
    const _meeting = await getMeeting(meetingId ?? meeting?._id);
    setCanChat(_meeting?.canchat);
  }

  useEffect(() => {
    teardownChatChannel();
    setupChatChannel();
  }, [onChatReload, onMeetingChat]);

  useEffect(() => {
    fetchAndSetChatLockState();
  }, []);

  return {
    toggleChatLock,
    loadMessages,
    init,
    checkIfUserIsBannedFromChat,
    setupChatChannel,
    handleSendExternalChatLink,
    showQr,
    loadedAllMessages,
    deleteMessages,
    onChatReload,
    setOnChatReload,
    onMeetingChat,
    setOnMeetingChat,
    onChatHighlight,
    setOnChatHighlight,
    onMessageDeleted,
    setOnMessageDeleted,
    canChat,
    teardownChatChannel,
  };
}
